import React, { useRef } from 'react';
import { Notifications } from '~/features/notifications/Notifications';
import { useIsDesktop } from '~/hooks/useMediaQuery';
import { AppScrollRootContext } from './appScrollRoot';
import { DesktopNavMenu } from './DesktopNavMenu';
import { MobileMenu } from './MobileMenu';
import { useRegisterMountedLayout, useSidebarNavOpen } from './sidebarState';
import type { RenderSideMenu } from './SideNav/types';

interface Props {
  children: React.ReactNode;
  mobileLogo?: 'mcv' | 'hlx';
  renderSideMenu?: RenderSideMenu;
  /** Optional role-aware destination for the mobile brand switcher. */
  switchTargetPath?: string;
  allowBrandSwitch?: boolean;
}

export const Layout = ({
  children,
  mobileLogo = 'mcv',
  renderSideMenu,
  switchTargetPath,
  allowBrandSwitch = true,
}: Props) => {
  const isDesktop = useIsDesktop();
  // [MCV-1919] Persisted, and shared with the floating Feedback button that sits beside the sidebar.
  const [isNavOpen, setIsNavOpen] = useSidebarNavOpen();
  useRegisterMountedLayout();
  // [MCV-1724] Handed to descendants (the in-app FAQ's scroll-spy) through AppScrollRootContext.
  const scrollRootRef = useRef<HTMLDivElement>(null);

  const getGridCols = () => {
    // Width.72 matches the VaultSideMenu/HlxSideMenu's w-72 inner container; keep these
    // in sync so there's no gap between the sidebar box and the main content column.
    if (isNavOpen) return 'min-[1360px]:grid-cols-[theme(width.72)_1fr]';
    return 'min-[1360px]:grid-cols-[56px_1fr]';
  };

  return (
    <>
      {!isDesktop && (
        <MobileMenu
          logo={mobileLogo}
          allowBrandSwitch={allowBrandSwitch}
          renderSideMenu={renderSideMenu}
          switchTargetPath={switchTargetPath}
        />
      )}

      <div className={`grid gap-3 min-w-[300px] h-full min-h-0 ${getGridCols()}`}>
        <div className="hidden min-[1360px]:flex h-full min-h-0 flex-col">
          <DesktopNavMenu
            isNavOpen={isNavOpen}
            setIsNavOpen={setIsNavOpen}
            renderSideMenu={renderSideMenu}
            switchTargetPath={switchTargetPath}
          />
        </div>

        {/* Main column: only this column scrolls internally on long pages,
            so the page shell (header + footer) stays pinned at the viewport
            edges and the sidebar never extends past the footer. `relative` keeps
            absolutely positioned descendants (including sr-only form labels)
            inside this scroll boundary instead of extending the document. */}
        <div ref={scrollRootRef} className="relative flex flex-col min-w-0 min-h-0 overflow-y-auto">
          <Notifications />
          <div className="min-w-0 px-4 min-[1360px]:px-6 py-6 flex flex-col flex-1 min-h-0">
            <AppScrollRootContext.Provider value={scrollRootRef}>
              {children}
            </AppScrollRootContext.Provider>
          </div>
        </div>
      </div>
    </>
  );
};
